import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { Flex, Box, ClickableBox, Tooltip } from 'components/ui';
import { OwnedNft } from 'utils/types';
import chevronUp from 'img/chevron-up.svg';
import chevronDown from 'img/chevron-down.svg';
import { BoxProps } from 'components/ui/Box';
import fullscreenIcon from 'img/fullscreen.svg';
import futuraPreviewSmall from 'img/futura_preview_small.png';

const OPENSEA_LINK = 'https://opensea.io/collection/eulerbeats-futura';

interface MixInputProps {
  name: string;
  label: string;
  value?: string;
  mixes: OwnedNft[];
  onChange: (e: ChangeProps) => void;
}

interface ChangeProps {
  target: {
    name: string;
    value: string;
  };
}

const Option = styled(Flex)<any>`
  cursor: pointer;

  ${props =>
    props.isDisabled
      ? `pointer-events: none;`
      : `

  &:hover {
    background-color: black; 
  }
`}
`;

const SLink = styled(Link)`
  color: white;
  margin: 0;
  padding: 0;

  & > img {
    margin-bottom: -4px;
  }
`;

const Underline = styled.span`
  text-decoration: underline;
`;

const MixInput = ({ name, label, mixes, value, onChange, ...props }: MixInputProps & BoxProps) => {
  const [open, setOpen] = useState(false);
  const mix: OwnedNft | undefined = value ? mixes.find(mix => mix.token_id === value) : undefined;

  const ref = useDetectClickOutside({
    onTriggered: () => setOpen(false),
  });

  const onSelect = useCallback(
    value => {
      onChange({
        target: {
          name,
          value,
        },
      });
    },
    [name, onChange]
  );

  return (
    <Box position="relative" width={['100%', '50%']} {...props}>
      <ClickableBox
        ref={ref}
        mb={2}
        color="white"
        onClick={() => setOpen(!open)}
        borderRadius="8px"
        overflow="hidden"
        backgroundColor="black"
      >
        <Flex alignCenter>
          <Flex background="black" fontSize="24px" width="58px" height="64px" alignCenter px={3}>
            {label}
          </Flex>
          <Flex
            background={open ? '#222' : 'black'}
            width="100%"
            height="80px"
            pl={1}
            pr={2}
            justifyBetween
            alignCenter
          >
            <Flex alignCenter>
              {mix ? (
                <>
                  <Box
                    data-for={`${mix?.metadata?.name}_preview`}
                    data-tip={`${mix?.metadata?.name}_tip`}
                    background={`url(${mix?.metadata?.image})`}
                    backgroundPosition="center"
                    backgroundSize="180px"
                    width="76px"
                    height="76px"
                  />
                  <Tooltip id={`${mix?.metadata?.name}_preview`} place="bottom">
                    <img alt={mix?.metadata?.name} src={mix?.metadata?.image} width="200px" />
                  </Tooltip>
                  <Box mx={3}>{mix?.metadata?.name}</Box>
                </>
              ) : (
                <Box ml={2}>Select Input Source</Box>
              )}
            </Flex>
            <Box mr={2}>
              <img alt={mix?.metadata?.name} src={open ? chevronUp : chevronDown} width="14px" />
            </Box>
          </Flex>
        </Flex>
      </ClickableBox>
      {open && (
        <Box
          position="absolute"
          zIndex={89}
          top="80px"
          left="54px"
          width="calc(100% - 58px)"
          maxHeight="210px"
          background="#444"
          overflowY="scroll"
          borderRadius="3px"
        >
          {mixes.map(mix => (
            <Option
              p={2}
              key={mix.token_id}
              width="100%"
              onClick={() => onSelect(mix.token_id)}
              alignCenter
            >
              <img alt={mix?.metadata?.name} src={mix?.metadata?.image} width="60px" />
              <Box ml={2}>{mix?.metadata?.name}</Box>
            </Option>
          ))}

          {mixes.length === 0 && (
            <Option isDisabled alignCenter p={2}>
              <img src={fullscreenIcon} alt="No Sources" width="24px" />
              <Box ml={3}>No input sources available</Box>
            </Option>
          )}

          <Option p={2} background="#2b2b2b" alignCenter>
            <SLink to={{ pathname: OPENSEA_LINK }} target="_blank">
              <img src={futuraPreviewSmall} alt="Futura preview" />
            </SLink>
            <SLink to={{ pathname: OPENSEA_LINK }} target="_blank">
              <Box px={3}>
                <Box mb={2} fontSize="16px">
                  Futura Mixes
                </Box>
                <Box fontSize="12px">
                  Find mixes to purchase and remix with on <Underline>OpenSea</Underline>
                </Box>
              </Box>
            </SLink>
          </Option>
        </Box>
      )}
    </Box>
  );
};

export default MixInput;
